import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, Zap, ShieldCheck, AlertTriangle,
    Bug, Activity, Loader2, Info,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import MetricDrawer from './MetricDrawer';
import DecisionSummaryBanner from './DecisionSummaryBanner';

/**
 * OverviewTabV2 — Decision-oriented overview answering "How are we doing right now?"
 *
 * Shows 5 grounded KPIs only. Weak/placeholder metrics removed from hero.
 * Trust labels on every card: LIVE, DERIVED, NOT INSTRUMENTED.
 * Links to relevant detail tabs.
 */

const OverviewTabV2 = ({ filters = {}, onTabChange }) => {
    const [overview, setOverview] = useState(null);
    const [watchData, setWatchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drawerMetric, setDrawerMetric] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const [overviewRes, watchRes] = await Promise.allSettled([
                adminAnalytics.getOverview(params),
                adminAnalytics.getWatchItems(params),
            ]);

            if (overviewRes.status === 'fulfilled') {
                setOverview(overviewRes.value.data);
            } else {
                const reason = overviewRes.reason;
                const status = reason?.response?.status;
                if (status === 401 || status === 403) {
                    throw Object.assign(new Error('auth'), { _type: 'auth' });
                }
                throw new Error(reason?.response?.data?.detail || 'Endpoint unavailable');
            }

            if (watchRes.status === 'fulfilled') setWatchData(watchRes.value.data);
        } catch (err) {
            if (err._type === 'auth') {
                setError('__auth__');
            } else {
                setError(err.response?.data?.detail || err.message || 'Failed to load overview data.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading overview…</span>
            </div>
        );
    }

    if (error === '__auth__') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <ShieldCheck size={24} style={{ color: '#f59e0b' }} />
                <p style={{ fontSize: 14, color: '#92400e', fontWeight: 500, margin: 0 }}>Session expired or not authenticated</p>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0, maxWidth: 360, textAlign: 'center' }}>
                    Log in with an admin account to view analytics data.
                </p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    const users = overview?.users || {};
    const engagement = overview?.engagement || {};
    const health = overview?.health || {};
    const feedback = overview?.feedback || {};

    const successRate = health.success_rate_pct ?? 100;
    const successColor = successRate >= 99 ? '#10b981' : successRate >= 95 ? '#f59e0b' : '#ef4444';

    const watchItemCount = watchData?.total ?? watchData?.items?.length ?? 0;
    const watchColor = watchItemCount > 0 ? '#f59e0b' : '#10b981';
    const openBugs = feedback.open_bugs ?? 0;

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Overview</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    How are we doing? · Last {filters.days || 7} days
                </p>
            </div>

            {/* Period-over-period decision signal */}
            <DecisionSummaryBanner filters={filters} />

            {/* ── Hero KPIs: 5 grounded metrics only ─────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 14, paddingBottom: 8,
                    borderBottom: '2px solid #6366f120',
                }}>
                    <Activity size={15} style={{ color: '#6366f1' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>Key Metrics</h3>
                    <span style={{
                        fontSize: 9, fontWeight: 600, letterSpacing: '0.06em',
                        color: '#6366f1', background: '#eef2ff',
                        padding: '2px 7px', borderRadius: 4,
                        border: '1px solid #c7d2fe',
                    }}>5 GROUNDED KPIs</span>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 12,
                }}>
                    {/* 1. Active Teachers */}
                    <KpiCard
                        label="Weekly Active Teachers"
                        value={users.active_last_n_days ?? 0}
                        icon={Users}
                        accent="#3b82f6"
                        trust="live"
                        sub={users.active_percentage != null
                            ? `${users.active_percentage}% of ${users.total_registered ?? 0} registered`
                            : null}
                        metricKey="active_users"
                        onClick={setDrawerMetric}
                    />

                    {/* 2. Lessons Generated */}
                    <KpiCard
                        label="Lessons Generated"
                        value={engagement.lessons_generated ?? 0}
                        icon={Zap}
                        accent="#10b981"
                        trust="live"
                        metricKey="lessons_generated"
                        onClick={setDrawerMetric}
                    />

                    {/* 3. Pipeline Success Rate */}
                    <KpiCard
                        label="Pipeline Health"
                        value={successRate}
                        format="percent"
                        icon={ShieldCheck}
                        accent={successColor}
                        trust="live"
                        sub={health.failures > 0 ? `${health.failures} failure${health.failures !== 1 ? 's' : ''} this period` : 'No failures this period'}
                        metricKey="success_rate"
                        onClick={setDrawerMetric}
                    />

                    {/* 4. Watch Items */}
                    <KpiCard
                        label="Open Watch Items"
                        value={watchItemCount}
                        icon={AlertTriangle}
                        accent={watchColor}
                        trust="live"
                        sub={watchItemCount > 0 ? 'Review in Live Ops →' : 'All clear'}
                        onCardClick={watchItemCount > 0 && onTabChange ? () => onTabChange('live-ops') : null}
                    />

                    {/* 5. Open Bugs */}
                    <KpiCard
                        label="Open Bugs"
                        value={openBugs}
                        icon={Bug}
                        accent={openBugs > 0 ? '#ef4444' : '#10b981'}
                        trust="live"
                        sub={feedback.open_improvements != null ? `+${feedback.open_improvements} improvements open` : null}
                        onCardClick={openBugs > 0 && onTabChange ? () => onTabChange('feedback-insights') : null}
                    />
                </div>

                {/* Trust footnote */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    marginTop: 10, fontSize: 11, color: '#94a3b8',
                }}>
                    <Info size={11} />
                    <span>
                        All 5 KPIs sourced from <code style={{ fontSize: 10, color: '#6366f1' }}>user_activity_daily</code> and deterministic pipeline counters.
                        Deeper metrics (retention, deliverable rate, session quality) are in their dedicated tabs.
                    </span>
                </div>
            </div>

            {/* ── Health & Feedback detail ─────────────────────────── */}
            <div style={{ marginBottom: 28 }}>
                <SectionLabel title="Health & Feedback Details" color={successColor} icon={ShieldCheck} />
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                    gap: 12,
                }}>
                    <MetricCard
                        label="Error Rate" value={health.error_rate_pct ?? '—'}
                        icon={AlertTriangle} accent="#ef4444" format="percent"
                        metricKey="error_rate" onClick={setDrawerMetric}
                    />
                    <MetricCard
                        label="Pipeline Failures" value={health.failures ?? 0}
                        icon={AlertTriangle} accent={health.failures > 0 ? '#ef4444' : '#10b981'}
                        metricKey="failures" onClick={setDrawerMetric}
                    />
                    <MetricCard
                        label="Open Improvements" value={feedback.open_improvements ?? 0}
                        icon={Activity} accent="#f59e0b"
                    />
                    <MetricCard
                        label="Feature Requests" value={feedback.open_feature_requests ?? 0}
                        icon={Zap} accent="#8b5cf6"
                    />
                </div>
            </div>

            {/* Note: Workspace targets (Deliverable Rate, Plan Tomorrow, T1L, etc.)
                are on the Teacher Value tab where they belong. */}

            {/* Metric Drawer */}
            {drawerMetric && (
                <MetricDrawer
                    metricKey={drawerMetric}
                    filters={filters}
                    onClose={() => setDrawerMetric(null)}
                />
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


// ─── KpiCard ─────────────────────────────────────────────────────────────────
// Hero metric card with explicit LIVE / DERIVED / NOT INSTRUMENTED label.

const TRUST_STYLES = {
    live:           { label: 'LIVE',             color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7' },
    derived:        { label: 'DERIVED',           color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd' },
    partial:        { label: 'PARTIAL DATA',      color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
    not_instrumented: { label: 'NOT INSTRUMENTED', color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb' },
};

const KpiCard = ({
    label, value, icon: Icon, accent = '#6366f1',
    trust = 'live', sub = null, format = 'number',
    metricKey = '', onClick = null, onCardClick = null,
}) => {
    const trustCfg = TRUST_STYLES[trust] || TRUST_STYLES.live;
    const displayValue = format === 'percent' && typeof value === 'number' ? `${value}%` : value ?? '—';
    const isClickable = !!(onClick || onCardClick);

    const handleClick = () => {
        if (onCardClick) onCardClick();
        else if (onClick && metricKey) onClick(metricKey);
    };

    return (
        <div
            onClick={handleClick}
            style={{
                background: '#fff',
                border: `1px solid #e5e7eb`,
                borderTop: `3px solid ${accent}`,
                borderRadius: 14,
                padding: '18px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minWidth: 0,
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
                cursor: isClickable ? 'pointer' : 'default',
                position: 'relative',
            }}
            onMouseOver={e => {
                if (isClickable) {
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.09)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }
            }}
            onMouseOut={e => {
                if (isClickable) {
                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'none';
                }
            }}
        >
            {/* Trust label — explicit text */}
            <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
                padding: '2px 6px', borderRadius: 4,
                background: trustCfg.bg, color: trustCfg.color,
                border: `1px solid ${trustCfg.border}`,
                alignSelf: 'flex-start',
            }}>
                {trustCfg.label}
            </span>

            {/* Label + icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {Icon && (
                    <div style={{
                        width: 26, height: 26, borderRadius: 7,
                        background: `${accent}14`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon size={13} style={{ color: accent }} />
                    </div>
                )}
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                </span>
            </div>

            {/* Value */}
            <div style={{
                fontSize: 28, fontWeight: 800, color: '#111827',
                fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
                {typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}
            </div>

            {/* Sub text */}
            {sub && (
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>{sub}</div>
            )}

            {/* Drill-down arrow */}
            {isClickable && (
                <div style={{
                    position: 'absolute', top: 10, right: 12,
                    fontSize: 11, color: '#94a3b8',
                }}>
                    ↗
                </div>
            )}
        </div>
    );
};


// ─── Shared sub-components ───────────────────────────────────────────────────

export const SectionHeader = ({ title, subtitle }) => (
    <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>{subtitle}</p>}
    </div>
);

export const Section = ({ title, icon: Icon, color = '#6366f1', children }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 12, paddingBottom: 8,
            borderBottom: `2px solid ${color}20`,
        }}>
            {Icon && <Icon size={15} style={{ color }} />}
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
        </div>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
        }}>
            {children}
        </div>
    </div>
);

const SectionLabel = ({ title, color, icon: Icon }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 12, paddingBottom: 8,
        borderBottom: `2px solid ${color}20`,
    }}>
        {Icon && <Icon size={15} style={{ color }} />}
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
    </div>
);

export const StatusBadge = ({ status = 'not_instrumented' }) => {
    const configs = {
        not_instrumented: { label: 'NOT INSTRUMENTED', color: '#94a3b8', bg: '#f1f5f9' },
        partial:          { label: 'PARTIAL DATA',     color: '#d97706', bg: '#fffbeb' },
        no_data:          { label: 'NO RECENT DATA',   color: '#6b7280', bg: '#f9fafb' },
        collecting:       { label: 'COLLECTING',       color: '#10b981', bg: '#ecfdf5' },
    };
    const cfg = configs[status] || configs.not_instrumented;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 9, fontWeight: 700, color: cfg.color,
            background: cfg.bg, padding: '2px 8px', borderRadius: 4,
            border: `1px solid ${cfg.color}30`,
            letterSpacing: '0.05em',
        }}>
            {cfg.label}
        </span>
    );
};

export const EmptyMetric = ({ label, reason = 'not_instrumented' }) => (
    <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
        padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 6,
    }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </span>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#d1d5db' }}>—</div>
        <StatusBadge status={reason} />
    </div>
);


export default OverviewTabV2;
